/**
 * Feature Flag Constants
 *
 * This file defines all feature flags used in the application.
 * Feature flags operate at two levels:
 *
 * 1. GLOBAL FLAGS (FEATURE_FLAGS enum):
 *    - Evaluated per user/role/doctor
 *    - Stored in DynamoDB FeatureFlag table
 *    - Gates whether a feature is available to the user at all
 *    - Accessed via useFeatureFlag() hook or isFeatureEnabled() utility
 *
 * 2. APPOINTMENT FLAGS (APPOINTMENT_FEATURE_FLAGS enum):
 *    - Evaluated per appointment
 *    - Stored in AppointmentRecord.featureFlags array
 *    - Gates whether a feature is enabled for a specific appointment
 *    - Toggled via Experimental Features modal
 *    - Accessed via featureFlagEnabledAtom() in appointment context
 *
 * A feature may have both global and appointment-level flags with the same key
 * but different gating behaviors.
 */

/**
 * Global feature flags evaluated at user/role/doctor level.
 * These control feature availability across the application.
 */
export enum FEATURE_FLAGS {
  /** Global switch for the daily Indy memory job; enable after importing backfill. */
  INDY_USER_MEMORY = "enable-indy-user-memory",

  /** Example flag for testing feature flag functionality */
  EXAMPLE_FEATURE = "example-feature",

  /**
   * Serve large medical records from their per-page images instead of PDF.js
   * (spec `pdf-preview-image-viewer.md`).
   *
   * Gates the whole routing path, not just the renderer: with it off a reader
   * issues neither the page-image lookup nor the size HEAD, and every record
   * opens exactly as it does today. It also tightens the per-file prefetch cap
   * to the routing threshold, since a record served from page images never
   * reads its own bytes.
   *
   * A pilot is genuinely informative here because the downside is "looks
   * different" rather than "tab dies": the image path loses text selection,
   * which is a no-op on scans and a real loss on text-native PDFs, and only
   * readers can say where that line falls. Kill switch is `enabled: false`.
   */
  PDF_IMAGE_VIEWER = "pdf-image-viewer",

  /**
   * Voice dictation in the Indy chat composer.
   * Internal-first rollout of streaming dictation, Indy only.
   */
  INDY_DICTATION = "indy-dictation",

  /**
   * LLM cleanup pass over each dictated utterance (POST /dictation/cleanup).
   * Rides on top of INDY_DICTATION. Split out so the streaming engine and the
   * cleanup pass can be compared side by side before rollout — the round trip
   * lands after the raw text, so the question it answers is whether the polish
   * is worth the beat it arrives late.
   */
  INDY_DICTATION_CLEANUP = "indy-dictation-cleanup",

  /**
   * Streaming dictation on the Assessment tab
   * (`docs/features/dictation/ARCHITECTURE.md`). When enabled for a user, the
   * Assessment floating mic negotiates the `assessment-streaming` surface:
   * live words as you speak, per-sentence reformat, and the LLM cleanup pass.
   * When disabled (default), Assessment keeps the turn-based session it has
   * always had, byte for byte.
   *
   * Targeted per-user so the first prod exposure is one pilot physician, whose
   * use doubles as the medical-accuracy measurement `gpt-live-transcribe` has
   * never had. Kill switch is `enabled: false`.
   *
   * This flag gates the cleanup pass on this surface too — Indy's separate
   * INDY_DICTATION_CLEANUP does not apply here. One pilot, one switch: two
   * flags would let the pilot run streaming without the plausibility guard
   * against fabricated numbers, which matters more in report content than in
   * chat, and measure the wrong thing.
   */
  ASSESSMENT_STREAMING_DICTATION = "assessment-streaming-dictation",

  /**
   * Exam dictation on the native Evaluation tab: in edit mode, a floating panel
   * dictates on the `exam-streaming` surface and an LLM fills the exam draft
   * from the whole transcript. Off (default) leaves the tab unchanged.
   */
  EXAM_DICTATION = "enabled_exam_dictation",

  /**
   * Fair medical assistant assignment algorithm.
   * When enabled, uses improved assignment logic for distributing work to MAs.
   */
  ENABLE_FAIR_MA_ASSIGNMENT = "enable-fair-ma-assignment",

  /**
   * Impairment Rating feedback feature.
   * Allows users to provide feedback on impairment rating calculations.
   */
  IR_FEEDBACK = "ir-feedback",

  /**
   * Impairment Rating: Almaraz-Guzman default. Same key the lambda reads
   * (services/feature_flags/flags.py). Seeds the manual-mode "Almaraz Guzman"
   * toggle and is what auto mode sends; the toggle itself is always shown.
   */
  IR_ALMARAZ_GUZMAN = "enabled_ir_ag",

  /**
   * Sub-rosa video summarization with Gemini. Same key the video
   * summarization batch and the summarize_sub_rosa_entry lambda read
   * (services/feature_flags/flags.py). Off hides "Add Sub Rosa entry with
   * summarization" — the lambda would refuse it anyway — leaving the plain
   * "Add Sub Rosa entry".
   */
  SUB_ROSA_VIDEO_SUMMARIZATION = "enabled_gemini_summarize_video",

  /**
   * Library/Learning section.
   * Controls access to the learning resources and documentation library.
   */
  LIBRARY = "library",

  /**
   * Physician onboarding decks in the Library Learning tab.
   * When enabled, shows the "Physician onboarding" PDF slide-deck section and
   * allows opening those decks. Rides on top of LIBRARY.
   */
  LIBRARY_PHYSICIAN_ONBOARDING = "library-physician-onboarding",

  /**
   * Physician Referral program.
   * Gates the physician-only Referral dashboard (nav tab + /referral route).
   */
  REFERRAL = "referral",

  /**
   * Redesigned physician Appointment overview tab.
   * When enabled (doctor view only), the Appointment tab renders the new
   * overview layout (claim summary, key events, current complaint, review
   * checklist) instead of the legacy appointment-info fields. Several sections
   * are LLM/lambda-backed and render placeholders until the backend lands.
   */
  DOCTOR_APPOINTMENT_OVERVIEW = "doctor-appointment-overview",

  /**
   * Backend only: the assessment generator feeds its diagnosis prompts the
   * per-diagnosis evidence statements from the diagnosis-evidence service
   * instead of the whole insight dicts. Listed here so the admin flag page can
   * create and toggle it; the portal never reads it.
   */
  ASSESSMENT_GENERATOR_DIAGNOSIS_EVIDENCE = "assessment-generator-diagnosis-evidence",

  /**
   * Assessment section statuses and section-switch validation.
   * When enabled, section status chips are shown and section-level validation
   * is evaluated on section transitions while editing.
   */
  ASSESSMENT_SECTION_STATUS = "assessment-section-status",

  /**
   * Assessment review inside the Assessment tab.
   * When enabled, MAs get a Review/Form segment on a submitted assessment and
   * land on the generated review rather than the questionnaire. Off: the review
   * stays reachable only through its /debug route.
   */
  ASSESSMENT_REVIEW_IN_TAB = "assessment-review-in-tab",

  /**
   * Visit split functionality.
   * Allows splitting appointments into multiple visits.
   */
  ENABLE_VISIT_SPLIT = "enable-visit-split",

  /**
   * Multi time slots in Inquiry tentative appointment form.
   * When enabled, Inquiry supports primary + additional time slots UI.
   * When disabled, Inquiry uses the legacy single time slot UI.
   */
  MULTI_TIME_SLOTS = "multi-time-slots",

  /**
   * Deposition hold flow.
   * When enabled, "Create deposition hold" replaces "Create deposition" in the
   * linked appointment menu, and DEPOSITION_HOLD status is supported with
   * multi-timeslot confirm flow.
   */
  DEPOSITION_HOLD = "deposition-hold",

  /**
   * Portal notifications panel in navbar/main layout.
   * Controls visibility of Notifications tab and NotificationBar.
   */
  NOTIFICATIONS = "notifications",

  /**
   * Agent chatbot in appointment details.
   * Controls visibility of the Chatbot sidebar button and modal.
   */
  ENABLED_CHATBOT = "enabled_chatbot",

  /**
   * Physician cohort for Indy, targeted by User.id. Doctors need it on top of
   * ENABLED_CHATBOT; the backend checks the same row for admission
   * (services/agent_chat/admission.py), so this only shapes the entry points.
   */
  ENABLED_INDY_PHYSICIAN = "enabled_indy_physician",

  /**
   * Gemini route for Indy, targeted by User.id. The backend rejects Gemini
   * requests while it is off; here it only shows the dev provider picker.
   */
  ENABLED_INDY_GEMINI = "enabled_indy_gemini",

  /**
   * Prior-opinion prepopulation on re-eval assessments
   * (spec `reeval-prior-opinion-prepopulation.md`).
   * Gates the per-section "My opinion is unchanged" OpinionCards in the
   * assessment workspace, fed by the PRIOR_OPINION_SNAPSHOT dangling
   * Assessment extracted from the prior evaluation's final report.
   */
  REEVAL_PRIOR_OPINION_PREPOPULATE = "reeval-prior-opinion-prepopulate",

  /**
   * HOI Introduction section (spec `hoi-introduction-recap.md`).
   * When enabled, the Patient info "Cover Letters" section becomes
   * "Introduction": one editable statement per party, fed by the per-file
   * recaps, plus a Closing paragraph. When disabled, the legacy per-PDF
   * recap list + consolidated intro paragraph is unchanged.
   *
   * The Closing's in-scope / refer-out lists come from the appointment's
   * claimed and examination body parts (both now unconditional).
   */
  INTRODUCTION_SECTION = "introduction-section",

  /**
   * History validation enforcement.
   * When enabled, auto-runs validation on Patient Info save and blocks
   * HOI generation with confirmation modals when unresolved issues exist.
   */
  ENABLE_HISTORY_VALIDATION_ENFORCEMENT = "enable-history-validation-enforcement",

  /**
   * MRR validation enforcement.
   * When enabled, "Mark as cleaned" on the Record Review tab first walks the
   * MA through any open ERROR-level MRR validation issues (per-doc, cross-doc
   * and history↔MRR cross-check) in a soft-gate modal wizard.
   */
  ENABLE_MRR_VALIDATION_ENFORCEMENT = "enable-mrr-validation-enforcement",

  /**
   * "Merge populated sources" on Patient Info (history merge, Flow B).
   * Gates the Populate dropdown option that reconciles each already-populated
   * note field (MA notes + history call + medical records + intake form) into
   * one value, appended at the bottom behind the "All Sources" marker.
   */
  ENABLE_HISTORY_MERGE_POPULATED_SOURCES = "enable-history-merge-populated-sources",

  /**
   * Use ECS API server for address formatting instead of Lambda.
   * When enabled, calls POST /address/format on the ECS API server.
   * When disabled, uses the FormatAddressFunction Lambda (original flow).
   */
  USE_ECS_ADDRESS_FORMAT = "use-ecs-address-format",

  /**
   * Normalized fax-number management for carrier and law firm contacts.
   * When enabled, appointment forms can select fax numbers from managed
   * contact entities while keeping legacy embedded fax fields available.
   */
  FAX_NUMBER_MANAGEMENT = "fax-number-management",

  /**
   * N110 single-party e-fax sending. Gates the "eFax" entry point on the N110
   * file row (opens the send modal). Independent of FAX_NUMBER_MANAGEMENT so the
   * structured number-management UI can ship ahead of the send flow. Assumes
   * management is already on (release order: management → single → batch).
   */
  FAX_SEND_SINGLE = "fax-send-single",

  /**
   * N110 bulk (batch) e-fax sending + the per-party delivery-status tracking UI.
   * Shipped after FAX_SEND_SINGLE (needs the status-tracking surface).
   */
  FAX_SEND_BATCH = "fax-send-batch",

  /**
   * Normalized phone-number management for carrier and law firm contacts.
   * Mirrors FAX_NUMBER_MANAGEMENT: when enabled, appointment forms can select
   * phone numbers (with an optional extension) from managed contact entities
   * while keeping the legacy embedded phone fields available for backward
   * compatibility.
   */
  PHONE_NUMBER_MANAGEMENT = "phone-number-management",

  /**
   * Use ECS API server for job duties generation instead of Lambda.
   * When enabled, calls POST /job-duties/generate on the ECS API server.
   * When disabled, uses the GenerateJobDutiesFunction Lambda.
   */
  USE_ECS_JOB_DUTIES = "use-ecs-job-duties",

  /**
   * Use ECS API server for MA auto-assignment instead of frontend logic.
   * When enabled, calls POST /ma-assignment/assign on the ECS API server.
   * When disabled, uses the legacy frontend hooks (fair or weighted random).
   */
  USE_ECS_MA_ASSIGNMENT = "use-ecs-ma-assignment",

  /**
   * Appointment-file reads, Wave 2 (batch readers): dash status cells, dash
   * checklist filters, CaseLibraryTab and related briefings read files from
   * POST /appointments/files/batch instead of the AppointmentFileReference
   * junction. `specs/appointment-file-reads-portal-cutover.md`.
   */
  USE_ECS_FILE_READS_BATCH = "use-ecs-file-reads-batch",

  /**
   * MA distribution dashboard in Ops Settings.
   * When enabled, shows the "MA distribution" tab with weekly capacity
   * distribution and assignment simulation (dry-run) functionality.
   */
  ENABLED_MA_DISTRIBUTION_DASHBOARD = "enabled-ma-distribution-dashboard",

  /**
   * All Cases dashboard view.
   * When enabled, shows the "All cases" tab in the MA/Admin navigation
   * with a case-centric dashboard listing all CaseRecords.
   */
  ALL_CASES_DASHBOARD = "all-cases-dashboard",

  /**
   * Cover Letter Review tab for physicians.
   * When enabled, shows the "Cover letter review" tab in the doctor
   * appointment view with per-provider cover letter recaps.
   */
  ENABLED_COVER_LETTER_REVIEW = "enabled-cover-letter-review",

  /**
   * Workflow audit follow-up actions on the appointment tab.
   * When enabled, the Appointment tab fetches PROPOSED WorkflowAuditActions
   * for the current appointment and auto-pops the firm/carrier verification
   * modal so an operator can resolve them. Each resolved action writes a
   * comment entry back to the parent WorkflowAudit.
   */
  ENABLED_WORKFLOW_AUDIT_ACTIONS = "enabled-workflow-audit-actions",

  /**
   * Per-prompt cost projection in the PromptsBrowser debug page.
   * Gates the CloudWatch-backed monthly cost bars + labels. Prod-only
   * gate — dev/stage always show the feature; prod hides it unless the
   * flag is enabled for the user/role.
   */
  PROMPT_COST_VIEWING = "prompt-cost-viewing",

  /**
   * Explicit interpreter confirmation status.
   * When enabled, interpreter status follows Missing → Vendor selected →
   * Confirmed, where Confirmed requires ops to manually mark it via the
   * "Mark as confirmed" control (interpreter.confirmed === true). Turns on the
   * dedicated Interpreter info section + status controls in the appointment
   * tab and the pre-visit / today / active drawers, and switches the email
   * `interpreter_confirmed` variable to the explicit confirmed value.
   * When disabled (default): legacy behavior — any filled interpreter field
   * counts as confirmed and the legacy Scheduled/Completed statuses apply.
   * Kept off until the backend `confirmed` backfill runs, then GA'd.
   */
  INTERPRETER_CONFIRMATION = "interpreter-confirmation",

  /**
   * Read appointment files via the UploadedFile.bySourceAppointment GSI with
   * read-time inheritance, instead of the legacy AppointmentFileReference
   * junction table. Portal companion to the backend
   * `enable-read-files-by-source-appointment` flag, but a distinct key so the
   * portal can roll out independently. Gates only the linked-appointment read
   * path (useLinkedAppointmentFiles / useAllAppointmentFiles); when off, files
   * are read from the junction table (referenceStatus written at reschedule
   * time). resolveFileOwnership derives read-only/ownership in both modes.
   */
  PORTAL_READ_FILES_BY_SOURCE_APPOINTMENT = "portal-read-files-by-source-appointment",

  /**
   * Report due date column on the physician-facing "Upcoming reports due"
   * (/active-cases) view. When enabled for a doctor, that doctor sees the
   * "Report due" column; other doctors have it hidden entirely. Admin/MA always
   * see the column regardless of this flag. Targeted per-doctor (via the user's
   * id) so it can be rolled out to a single physician (e.g. Josh) first.
   */
  DOCTOR_REPORT_DUE_COLUMN = "doctor-report-due-column",

  /**
   * IME appointment-family record UI. When enabled, IME-family appointments
   * (IME / IME_REEVALUATION / IME_ADR / IME_ADR_REEVALUATION) render the
   * dedicated IME layout: a reduced tab set, an Appointment tab that folds in
   * the inquiry-style "Tentative appointment" + "Upload intake form" sections,
   * a trimmed Claim section (Date of injury / Claim # / Exam body parts),
   * "Plaintiff attorney" instead of "Applicant attorney", no Nurse Case
   * Manager section, and the ImeReportMedicalRecordsModal on Generate report.
   * When disabled, IME appointments keep the legacy QME-shaped layout.
   * Default off, rolled out dev → stage → prod.
   */
  IME_RECORD = "ime-record",

  /**
   * Partial employment dates (GET-910). When enabled, the employment date
   * fields let an MA record a year or a month instead of forcing an exact day,
   * storing the pair in `dateOfHireFlexible` / `dateOfTerminationFlexible`.
   * When disabled, the day-only picker is shown — writes still populate both
   * columns, always at DAY precision. Gates capture only, never reads, so
   * flipping it off never hides a date that was already recorded.
   */
  FLEXIBLE_EMPLOYMENT_DATES = "flexible-employment-dates",

  /**
   * Capture recalled injury dates alongside the exact panel/demo dates
   * (GET-3720). Reads always hydrate both so turning capture off cannot erase
   * a previously entered partial date on an unrelated save.
   */
  FLEXIBLE_INJURY_DATES = "flexible-injury-dates",

  /**
   * Physician onboarding call questionnaire. When enabled, the physician page
   * header shows the "Onboarding questions" button, which opens the
   * "Onboarding call" modal (addresses, contacts, scheduling preferences).
   * When disabled (default), neither the button nor the modal exists.
   *
   * UI-only for now — the answers have no backend, so the form keeps its draft
   * in memory and Save does not persist. Kept behind the flag until the
   * storage and mutation land.
   */
  PHYSICIAN_ONBOARDING_CALL = "physician-onboarding-call",

  /**
   * Indigo Doc `/docs` sandbox (specs/indigo-doc/poc.md): the composed-report
   * prototype at /docs/report_prototype, then the POC. Synthetic content only; on in
   * dev only.
   */
  INDIGO_DOC_SANDBOX = "enable-indigo-doc-sandbox",

  /**
   * Search bars call the api-server `/search/*` routes instead of the AppSync
   * VTL resolvers, so each OpenSearch request runs through AsyncSearch, where
   * the OpenSearchAudit writer will land. Migrated one bar at a time; today
   * only LocationSearchBar.
   * When disabled (default), every bar stays on AppSync.
   */
  SEARCH_BAR_ECS_ENDPOINT = "enabled_search_bar_ecs_endpoint",

  /**
   * Upload tab: dropped files open a "Review uploaded files" step where the MA
   * picks who sent them and confirms each file type; files are stored under
   * the naming convention ("cover letter_ADJ.pdf"). Skipped when every name
   * already carries its type and party.
   * When disabled (default), files upload straight away under their own names.
   */
  UPLOAD_FILE_TYPE_REVIEW = "enabled-upload-file-type-review",
}

/**
 * Appointment-level feature flags.
 * These can be toggled per-appointment via the Experimental Features modal.
 *
 * Unlike global flags which gate feature availability, appointment flags control
 * feature behavior for a specific appointment. A user might have global access to
 * a feature but choose to enable/disable it per appointment.
 */
export enum APPOINTMENT_FEATURE_FLAGS {
  /**
   * Use the native structured Physical Exam surface for this appointment's
   * Evaluation tab, replacing the legacy Google-Doc iframe. When enabled, the
   * Evaluation tab renders the doctor's PhysicalEvalTemplate as a fillable form
   * and (Slice 2+) persists answers on the appointment; when disabled
   * (default), the legacy iframe is shown — no regression.
   *
   * This is the per-appointment rollout gate the configurator's global
   * PHYSICAL_EVAL_TEMPLATE flag deferred to appointment-side work — read here
   * and (follow-on M1.6) by the backend (Impairment Rating + Final Report).
   * See specs/physical-eval-template/appointment-surface.md.
   */
  USE_NATIVE_PHYSICAL_EXAM = "use-native-physical-exam",

  /**
   * Report scheduled comms on Final submission for this appointment.
   * When enabled, the Final submission modal is a single step: pick report
   * content + recipient parties + send timing (immediately vs next business
   * day at 9 AM), then "Generate & send" hands the packet build and the sends
   * to ReportDeliveryWorkflow, which emails each party through Zendesk. When
   * disabled (default), the legacy two-step create-package → "Draft emails"
   * flow (generateReportEmailFunction) is shown.
   *
   * Per-appointment because the rollout is an opt-in pilot: an MA turns it on
   * for the appointment they want to send through the new flow.
   */
  REPORT_SCHEDULED_COMMS = "enable-report-scheduled-comms",

  /**
   * Native History of Injury on Indigo Doc (specs/indigo-doc/hoi-migration.md
   * H1). When enabled and the appointment has a native HOI, the HOI tab opens
   * it in the Indigo Doc editor; otherwise (or with no native HOI yet) the tab
   * keeps the Google Doc iframe. The backend reads the same key
   * (`services/feature_flags/flags.py` INDIGO_DOC_HOI_FLAG) to pick the HOI
   * source, so the tab and every reader agree.
   */
  INDIGO_DOC_HOI = "enable-indigo-doc-hoi",

  /**
   * For unit-testing only.
   */
  TEST_FLAG = "test-flag",
  TEST_FLAG_2 = "test-flag-2",
}

/**
 * Backward compatibility feature flags.
 * These are long-lived flags set automatically by backend processes or backfill scripts
 * to indicate data characteristics of an appointment. They are NOT toggleable
 * in the Experimental Features modal.
 *
 * Unlike APPOINTMENT_FEATURE_FLAGS which are user-controlled toggles,
 * these flags reflect the state of the appointment's data and are read-only
 * in the frontend.
 */
export enum BACKWARD_COMPAT_FLAGS {
  /**
   * Indicates this appointment has legacy medical record data — the briefing
   * exists only as the original Google Doc, never converted to V2 segments.
   * Set on genuinely-legacy appointments by the reverse_export tooling.
   * When enabled: surfaces the "Medical record briefing doc (V1)" three-dot menu
   * item linking to that doc. (The old legacy Medical Record tab it used to
   * gate has been removed.)
   */
  LEGACY_MEDICAL_RECORD_DATA = "legacy-medical-record-data",
}
